import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { RepairBillService } from '../../services/maintenance.service';
import { SupplierService } from '../../services/supplier.service';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { RepairBillDTO, RepairBillFile, RepairBillSaveRequest, RepairBillStatus } from '../../interfaces/maintenance';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { saveBlob } from '../../utils/saveBlob';
import { formatMoney } from '../../utils/format';
import { fmtDateTime } from './woCommon';

export const REPAIR_BILL_COLOR: Record<RepairBillStatus, 'default' | 'warning' | 'success' | 'error'> = {
    DRAFT: 'default', SUBMITTED: 'warning', APPROVED: 'success', REJECTED: 'error',
};
const LABEL: Record<RepairBillStatus, string> = {
    DRAFT: 'Draft', SUBMITTED: 'Waiting for accounts', APPROVED: 'Approved', REJECTED: 'Rejected',
};
export const repairBillLabel = (s: RepairBillStatus): string => LABEL[s];

interface Props {
    workOrderId: string;
    /** Holder of MAINTENANCE_REPAIR_BILL_SUBMIT: may add, edit, attach and submit. Others only read. */
    canSubmit: boolean;
    /** Work order is Closed or Cancelled: no new bills. */
    live: boolean;
}

/** Repair bills a work order has sent (or is about to send) to accounts for approval. Maintenance never posts a journal. */
const RepairBillsPanel: React.FC<Props> = ({ workOrderId, canSubmit, live }) => {
    const toast = useToast();
    const [bills, setBills] = useState<RepairBillDTO[]>([]);
    const [files, setFiles] = useState<Record<string, RepairBillFile[]>>({});
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [editing, setEditing] = useState<RepairBillDTO | 'new' | null>(null);
    const [saving, setSaving] = useState(false);
    const [supplierId, setSupplierId] = useState('');
    const [vendorName, setVendorName] = useState('');
    const [amount, setAmount] = useState('');
    const [billRef, setBillRef] = useState('');
    const [billDate, setBillDate] = useState('');
    const [note, setNote] = useState('');
    const fileInput = useRef<HTMLInputElement | null>(null);
    const [uploadFor, setUploadFor] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const list = await RepairBillService.listForWorkOrder(workOrderId);
            setBills(list);
            const entries = await Promise.all(list.map(async b => [b.id, await RepairBillService.listFiles(b.id)] as const));
            setFiles(Object.fromEntries(entries));
        } catch (e) {
            toast.showApiError(e, 'Failed to load repair bills');
        }
    }, [workOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!canSubmit) return;
        SupplierService.listByInstitution(0, 200).then(r => setSuppliers(r.content)).catch(() => setSuppliers([]));
    }, [canSubmit]);

    if (!canSubmit && bills.length === 0) return null;

    const openEditor = (b: RepairBillDTO | 'new') => {
        setEditing(b);
        setSupplierId(b === 'new' ? '' : b.supplierId ?? '');
        setVendorName(b === 'new' ? '' : b.vendorName ?? '');
        setAmount(b === 'new' ? '' : String(b.amount));
        setBillRef(b === 'new' ? '' : b.billRef ?? '');
        setBillDate(b === 'new' ? '' : b.billDate ?? '');
        setNote(b === 'new' ? '' : b.note ?? '');
    };

    const save = async () => {
        const req: RepairBillSaveRequest = {
            supplierId: supplierId || null, vendorName: vendorName.trim() || null, amount: Number(amount),
            billRef: billRef.trim() || null, billDate: billDate || null, note: note.trim() || null,
        };
        try {
            setSaving(true);
            if (editing === 'new') await RepairBillService.create(workOrderId, req);
            else if (editing) await RepairBillService.update(editing.id, req);
            toast.showSuccess('Repair bill saved');
            setEditing(null);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to save the repair bill');
        } finally {
            setSaving(false);
        }
    };

    const act = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
        try {
            await fn();
            toast.showSuccess(ok);
            await load();
        } catch (e) {
            toast.showApiError(e, fail);
        }
    };

    const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        const id = uploadFor;
        e.target.value = '';
        if (!file || !id) return;
        await act(() => RepairBillService.uploadFile(id, file), 'File attached', 'Failed to attach the file');
    };

    const download = async (billId: string, f: RepairBillFile) => {
        try {
            const { blob, fileName } = await RepairBillService.downloadFile(billId, f.id);
            saveBlob(blob, fileName || f.fileName);
        } catch (e) {
            toast.showApiError(e, 'Failed to download the file');
        }
    };

    const valid = Number(amount) > 0 && (supplierId !== '' || vendorName.trim() !== '');
    const editable = (b: RepairBillDTO) => canSubmit && (b.status === 'DRAFT' || b.status === 'REJECTED');

    return (
        <Paper sx={{ p: 2, mb: 2 }} data-help-section="maint.repairBill.section">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1">Repair bills to accounts</Typography>
                {canSubmit && live && (
                    <Button data-help="maint.repairBill.add" size="small" onClick={() => openEditor('new')}>Add repair bill</Button>
                )}
            </Box>
            <input ref={fileInput} type="file" hidden aria-label="Vendor bill file" data-testid="repair-bill-file" accept=".pdf,.png,.jpg,.jpeg" onChange={onFile} />
            {bills.length === 0 && <Typography variant="body2" color="text.secondary">No repair bills yet.</Typography>}
            {bills.map(b => (
                <Box key={b.id} sx={{ borderTop: 1, borderColor: 'divider', py: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        <Chip size="small" color={REPAIR_BILL_COLOR[b.status]} label={repairBillLabel(b.status)} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatMoney(b.amount)}</Typography>
                        <Typography variant="body2">{b.supplierName ?? b.vendorName ?? '-'}</Typography>
                        {b.billRef && <Typography variant="body2" color="text.secondary">Ref {b.billRef}</Typography>}
                        {b.fixedAssetCode && (
                            <Typography variant="body2" color="text.secondary">Asset {b.fixedAssetCode}{b.fixedAssetName ? ` ${b.fixedAssetName}` : ''}</Typography>
                        )}
                    </Box>
                    {b.note && <Typography variant="caption" display="block">{b.note}</Typography>}
                    {b.status === 'REJECTED' && b.rejectReason && (
                        <Alert severity="error" sx={{ mt: 1 }}>Rejected{b.decidedByName ? ` by ${b.decidedByName}` : ''}: {b.rejectReason}</Alert>
                    )}
                    {b.status === 'SUBMITTED' && (
                        <Typography variant="caption" display="block" color="text.secondary">Sent {fmtDateTime(b.submittedAt)}</Typography>
                    )}
                    {b.status === 'APPROVED' && b.supplierBillId && (
                        <Typography variant="caption" display="block">
                            Approved{b.decidedAt ? ` ${fmtDateTime(b.decidedAt)}` : ''}. <RouterLink to={`/procurement/supplier-bills/${b.supplierBillId}`}>Open the supplier bill</RouterLink>
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 0.5 }}>
                        {(files[b.id] ?? []).map(f => (
                            <Box key={f.id} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Button size="small" onClick={() => download(b.id, f)}>{f.fileName}</Button>
                                {editable(b) && (
                                    <IconButton size="small" aria-label={`Remove ${f.fileName}`} data-help="maint.repairBill.removeFile"
                                        onClick={() => act(() => RepairBillService.deleteFile(b.id, f.id), 'File removed', 'Failed to remove the file')}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        ))}
                        {editable(b) && (
                            <Button data-help="maint.repairBill.attach" size="small" onClick={() => { setUploadFor(b.id); fileInput.current?.click(); }}>Attach vendor bill</Button>
                        )}
                        {editable(b) && <Button data-help="maint.repairBill.edit" size="small" onClick={() => openEditor(b)}>Edit</Button>}
                        {editable(b) && (
                            <Button data-help="maint.repairBill.submit" size="small" variant="contained"
                                onClick={() => act(() => RepairBillService.submit(b.id), 'Sent to accounts', 'Failed to send the repair bill')}>
                                {b.status === 'REJECTED' ? 'Resubmit to accounts' : 'Submit to accounts'}
                            </Button>
                        )}
                        {editable(b) && (
                            <Button data-help="maint.repairBill.delete" size="small" color="error"
                                onClick={() => act(() => RepairBillService.remove(b.id), 'Repair bill deleted', 'Failed to delete the repair bill')}>Delete</Button>
                        )}
                    </Box>
                </Box>
            ))}

            <Dialog open={editing !== null} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing === 'new' ? 'Add repair bill' : 'Edit repair bill'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pt: 1 }}>
                        <Box sx={{ flex: '1 1 240px', maxWidth: 400 }} data-help="maint.repairBill.field.supplier">
                            <SearchableSelect<Supplier> options={suppliers} value={supplierId} size="small" label="Supplier"
                                getOptionLabel={s => s.name} onChange={id => setSupplierId(id)} />
                        </Box>
                        <TextField data-help="maint.repairBill.field.vendor" size="small" label="Vendor name (if not a supplier)" value={vendorName}
                            onChange={e => setVendorName(e.target.value)} sx={{ flex: '1 1 240px', maxWidth: 400 }} />
                        <TextField data-help="maint.repairBill.field.amount" size="small" label="Amount" type="number" required value={amount}
                            onChange={e => setAmount(e.target.value)} sx={{ flex: '1 1 140px', maxWidth: 200 }} />
                        <TextField data-help="maint.repairBill.field.billRef" size="small" label="Vendor bill ref" value={billRef}
                            onChange={e => setBillRef(e.target.value)} sx={{ flex: '1 1 160px', maxWidth: 240 }} />
                        <TextField data-help="maint.repairBill.field.billDate" size="small" label="Bill date" type="date" value={billDate}
                            onChange={e => setBillDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                        <TextField data-help="maint.repairBill.field.note" size="small" label="Note" multiline minRows={2} value={note}
                            onChange={e => setNote(e.target.value)} sx={{ flex: '1 1 300px', maxWidth: 600 }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(null)}>Cancel</Button>
                    <Button variant="contained" disabled={!valid || saving} onClick={save}>Save</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default RepairBillsPanel;
